import classNames from "classnames/bind";
import React, { useCallback, useState } from "react";
import { useToast } from "../Toast";
import Button from "../Button/Button";
import DataTable from "../DataTable/DataTable";
import { CellArguments } from "../DataTable/types";
import TextInputBare from "../DataInput/TextInput/TextInput";
import Modal from "../Modal/Modal";
import Panel from "../Panel/Panel";
import Title from "../Title/Title";
import styles from "./FilterPanel.module.scss";
import { ManageFiltersModalProps, SavedFilterType } from "./types";

const cx = classNames.bind(styles);

/**
 * @deprecated part of the old FilterDrawer pattern. Use new FilterArea pattern
 * directly the apps instead.
 */
const ManageFiltersModal = ({
  isOpen,
  onClose,
  onEditFilter,
  savedFilters,
  onDeleteFilter,
}: ManageFiltersModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [input, setInput] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<
    | {
        id: string;
        name: string;
      }
    | undefined
  >(undefined);

  const { toast } = useToast();

  const renderNameComponent = ({ item }: CellArguments<SavedFilterType>) => {
    return item.id !== selectedFilter?.id ? (
      item?.name
    ) : (
      <TextInputBare
        name=""
        value={input}
        onChange={setInput}
      />
    );
  };

  const renderActionComponent = ({ item }: CellArguments<SavedFilterType>) => {
    return item.id !== selectedFilter?.id ? null : (
      <div className={cx("inlineTableButtons")}>
        <Button
          onClick={() => setSelectedFilter(undefined)}
          disabled={isSaving}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={handleOnSaveClick}
          isLoading={isSaving}
        >
          Save
        </Button>
      </div>
    );
  };

  const handleOnEditClick = (item: { id: string; name: string }) => {
    setSelectedFilter(item);
    setInput(item.name);
  };

  const handleOnSaveClick = async () => {
    if (selectedFilter?.id && onEditFilter) {
      try {
        setIsSaving(true);
        await onEditFilter(selectedFilter.id, { name: input });

        toast.push({
          intent: "success",
          description: `Successfully edited filter ${input}.`,
        });

        setSelectedFilter(undefined);
        setIsSaving(false);
      } catch (err) {
        setIsSaving(false);
        toast.push({
          intent: "error",
          description: `Unable to edit filter ${selectedFilter.name}.`,
        });
      }
    }
  };

  const handleDeleteClick = async (filter: { id: string; name: string }) => {
    if (onDeleteFilter) {
      try {
        setIsDeleting(true);

        await onDeleteFilter(filter?.id);
        toast.push({
          intent: "success",
          description: `Successfully deleted filter ${filter.name}.`,
        });
      } catch (err) {
        toast.push({
          intent: "error",
          description: `Unable to delete filter ${filter.name}`,
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const getItemActions = useCallback(
    ({ item }) => {
      return [
        ...(onEditFilter
          ? [
              {
                label: "Edit",
                buttonProps: {
                  onClick: () => handleOnEditClick(item),
                },
              },
            ]
          : []),
        ...(onDeleteFilter
          ? [
              {
                label: "Delete",
                buttonProps: {
                  onClick: () => handleDeleteClick(item),
                },
              },
            ]
          : []),
      ];
    },
    [onEditFilter, onDeleteFilter]
  );

  const headers = [
    {
      label: "Filter",
      key: "name",
      minWidth: 200,
      renderComponent: renderNameComponent,
    },
    ...(selectedFilter?.id
      ? [
          {
            label: "Action",
            key: "action",
            renderComponent: renderActionComponent,
          },
        ]
      : []),
  ];

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
    >
      <Modal.Header>
        <Title>Manage Saved Filters</Title>
      </Modal.Header>

      <Modal.Body>
        <Panel>
          <DataTable
            isLoading={isDeleting}
            headers={headers}
            items={savedFilters}
            getItemId={(item) => item.id}
            getItemActions={!selectedFilter?.id ? getItemActions : undefined}
          />
        </Panel>
      </Modal.Body>
    </Modal>
  );
};

export default ManageFiltersModal;
